import React, { lazy } from "react";
import { Route } from "react-router-dom";

import ProtectedRoute from "./ProtextedRoute";
import NotFound from "@/pages/Error/NotFound";
import { routes } from "@/config/routes";
import { AccountType, UserRole } from "@/types";
// import Dashboard from "@/pages/dashboard";

const ProductDetails = lazy(() => import("@/pages/Products/Details"));
const Products = lazy(() => import("@/pages/Products"));
const Quotes = lazy(() => import("@/pages/Quotes"));
const QuoteDetails = lazy(() => import("@/pages/Quotes/Details"));
const QuoteCheckout = lazy(() => import("@/pages/Quotes/Checkout"));
const CreateOpportunity = lazy(() => import("@/pages/Opportunities/Create"));
const Opportunities = lazy(() => import("@/pages/Opportunities"));
const OpportunityDetails = lazy(() => import("@/pages/Opportunities/Details"));

const UserProfile = lazy(() => import("@/pages/UserProfile"));
const Orders = lazy(() => import("@/pages/Orders"));
const OrderDetails = lazy(() => import("@/pages/Orders/Details"));
const Wallet = lazy(() => import("@/pages/Wallet"));

const LeadCreate = lazy(() => import("@/pages/Leads/Create"));
const LeadDetails = lazy(() => import("@/pages/Leads/Details/index"));
const LeadConvert = lazy(() => import("@/pages/Leads/Convert"));
const Leads = lazy(() => import("@/pages/Leads"));
const UploadLeads = lazy(() => import("@/pages/Leads/Upload"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactCreate = lazy(() => import("@/pages/Contacts/Create"));
const ContactDetails = lazy(() => import("@/pages/Contacts/Details"));

const CustomerAccounts = lazy(() => import("@/pages/CustomerAccounts"));
const CustomerAccountDetails = lazy(() => import("@/pages/CustomerAccounts/Details"));
const CustomerAccountCreate = lazy(() => import("@/pages/CustomerAccounts/Create"));

const Login = lazy(() => import('@/pages/auth/LogIn'));
const Proposals = lazy(() => import("@/pages/Proposals"));
const ProposalDetails = lazy(() => import("@/pages/Proposals/Details"));
// Lazy only: the editor chunk (v2/edit, @dnd-kit, Quill bubble) never loads with the app (editChunk guard).
const ProposalEditor = lazy(() => import("@/pages/Proposals/Editor"));

const Documents = lazy(() => import("@/pages/Documents"));
const CustomerFeedback = lazy(() => import("@/pages/CustomerFeedback"));
const McpAuthorize = lazy(() => import("@/pages/McpAuthorize"));

export const appRoutes = (
    <React.Fragment>
        <Route path="/customer-feedback" element={<ProtectedRoute><CustomerFeedback /></ProtectedRoute>} />
        {/* OAuth consent for MCP clients (ChatGPT / Claude connectors). */}
        {/* Not wrapped in ProtectedRoute: the page sends a logged-out user to
            /login?redirect=… itself, so the OAuth request survives the login. */}
        <Route path={routes.mcp.authorize} element={<McpAuthorize />} />
        {/* Redirect to root */}
        <Route
            path="/"
            element={
                <ProtectedRoute />
            }
        />
        <Route
            path={routes.auth.login}
            element={<Login />}
        />

        {/* Contacts */}
        <Route
            path={routes.contacts.root}
            element={
                <ProtectedRoute>
                    <Contacts />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.contacts.create}
            element={
                <ProtectedRoute>
                    <ContactCreate />
                </ProtectedRoute>
            }
        />

        <Route
            path={routes.contacts.details}
            element={
                <ProtectedRoute>
                    <ContactDetails />
                </ProtectedRoute>
            }
        />
        {/* Orders */}
        <Route
            path={routes.orders.root}
            element={
                <ProtectedRoute>
                    <Orders />
                </ProtectedRoute>
            }
        />

        <Route
            path={routes.orders.details}
            element={
                <ProtectedRoute>
                    <OrderDetails />
                </ProtectedRoute>
            }
        />
        {/* Leads */}
        <Route
            path={routes.leads.root}
            element={
                <ProtectedRoute>
                    <Leads />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.leads.upload}
            element={
                <ProtectedRoute>
                    <UploadLeads />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.leads.convert}
            element={
                <ProtectedRoute>
                    <LeadConvert />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.leads.details}
            element={
                <ProtectedRoute>
                    <LeadDetails />
                </ProtectedRoute>
            }
        />

        <Route
            path={routes.leads.add}
            element={
                <ProtectedRoute>
                    <LeadCreate />
                </ProtectedRoute>
            }
        />
        {/* Quotes */}
        <Route
            path={routes.quotes.root}
            element={
                <ProtectedRoute>
                    <Quotes />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.quotes.details}
            element={
                <ProtectedRoute>
                    <QuoteDetails />
                </ProtectedRoute>
            }
        />

        <Route
            path={routes.quotes.checkout}
            element={
                <ProtectedRoute>
                    <QuoteCheckout />
                </ProtectedRoute>
            }
        />
        {/* Proposals list (internal - INTERNAL accounts only) */}
        <Route
            path={routes.proposals.root}
            element={
                <ProtectedRoute allowedAccountTypes={[AccountType.INTERNAL]}>
                    <Proposals />
                </ProtectedRoute>
            }
        />
        {/* Proposal details (internal view/edit - INTERNAL accounts only) */}
        <Route
            path={routes.proposals.details}
            element={
                <ProtectedRoute allowedAccountTypes={[AccountType.INTERNAL]}>
                    <ProposalDetails />
                </ProtectedRoute>
            }
        />
        {/* Proposal document editor (Track C1 - INTERNAL accounts only, same guard as details) */}
        <Route
            path={routes.proposals.edit}
            element={
                <ProtectedRoute allowedAccountTypes={[AccountType.INTERNAL]}>
                    <ProposalEditor />
                </ProtectedRoute>
            }
        />
        {/* Products */}
        <Route
            path={routes.products.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN, UserRole.CHANNEL_ADMIN, UserRole.CHANNEL_SALES_REP]}>
                    <Products />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.products.details}
            element={
                <ProtectedRoute>
                    <ProductDetails />
                </ProtectedRoute>
            }
        />

        {/* Opportunities */}
        <Route
            path={routes.opportunities.create}
            element={
                <ProtectedRoute>
                    <CreateOpportunity />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.opportunities.root}
            element={
                <ProtectedRoute>
                    <Opportunities />
                </ProtectedRoute>
            }
        />


        <Route
            path={routes.opportunities.details}
            element={
                <ProtectedRoute>
                    <OpportunityDetails />
                </ProtectedRoute>
            }
        />

        {/* Wallet */}
        <Route
            path={routes.wallet.root}
            element={
                <ProtectedRoute>
                    <Wallet />
                </ProtectedRoute>
            }
        />

        {/* Profile */}
        <Route
            path={routes.profile.root}
            element={
                <ProtectedRoute>
                    <UserProfile />
                </ProtectedRoute>
            }
        />


        {/* Customer Accounts */}
        <Route
            path={routes.customers.root}
            element={
                <ProtectedRoute>
                    <CustomerAccounts />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.customers.create}
            element={
                <ProtectedRoute>
                    <CustomerAccountCreate />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.customers.details}
            element={
                <ProtectedRoute>
                    <CustomerAccountDetails />
                </ProtectedRoute>
            }
        />
        {/* Documents */}
        <Route
            path={routes.documents.root}
            element={
                <ProtectedRoute>
                    <Documents />
                </ProtectedRoute>
            }
        />

        <Route path="*" element={<NotFound />} />
    </React.Fragment>
);
